export type DraftAttachmentSource = "file" | "screen_recording";
export type DraftAttachmentStatus = "uploading" | "ready" | "failed";
export type DeliveryReceiptStatus = "idle" | "uploading" | "ready" | "sending" | "sent" | "failed";

export interface DraftAttachmentState {
  status: DraftAttachmentStatus;
  uploadId?: string;
  storagePath?: string;
  fileName: string;
  byteSize: number;
  caption: string;
  source: DraftAttachmentSource;
  progress: number;
  error?: string;
}

export interface RecoveredAttachmentInput {
  localId: string;
  fileName: string;
  byteSize: number;
  caption: string;
  source: DraftAttachmentSource;
}

export interface ComposerSubmitStateInput {
  busy: boolean;
  recording: boolean;
  isRequest: boolean;
  subject: string;
  body: string;
  attachments: DraftAttachmentState[];
}

export interface ComposerSubmitState {
  canSubmit: boolean;
  reason: string | null;
}

export function getComposerSubmitState(input: ComposerSubmitStateInput): ComposerSubmitState {
  if (input.busy) return { canSubmit: false, reason: "Orbit is sending your message." };
  if (input.recording) return { canSubmit: false, reason: "Stop recording before sending." };

  const hasUploadingAttachment = input.attachments.some((attachment) => attachment.status === "uploading");
  if (hasUploadingAttachment) {
    return { canSubmit: false, reason: "Wait for the file upload to finish before sending." };
  }

  const hasFailedAttachment = input.attachments.some((attachment) => attachment.status === "failed");
  if (hasFailedAttachment) {
    return { canSubmit: false, reason: "Retry or remove failed uploads before sending." };
  }

  const hasReadyAttachment = input.attachments.some((attachment) => attachment.status === "ready");
  const hasBody = input.body.trim().length > 0;
  const hasSubject = input.subject.trim().length > 0;
  const hasRequiredText = input.isRequest ? hasSubject || hasBody || hasReadyAttachment : hasBody || hasReadyAttachment;
  return hasRequiredText ? { canSubmit: true, reason: null } : { canSubmit: false, reason: null };
}

export function getAttachmentFallbackBody(attachments: DraftAttachmentState[]): string {
  const readyAttachments = attachments.filter((attachment) => attachment.status === "ready");
  if (!readyAttachments.length) return "";

  const recordingCount = readyAttachments.filter((attachment) => attachment.source === "screen_recording").length;
  if (recordingCount === readyAttachments.length) {
    return recordingCount === 1
      ? "A screen recording with audio was sent through Orbit Bridge."
      : `${recordingCount} screen recordings with audio were sent through Orbit Bridge.`;
  }

  return `Attached ${readyAttachments.length} file${readyAttachments.length === 1 ? "" : "s"} for review.`;
}

export function createRecoveredAttachmentState(input: RecoveredAttachmentInput): DraftAttachmentState {
  return {
    status: "failed",
    fileName: input.fileName,
    byteSize: input.byteSize,
    caption: input.caption,
    source: input.source,
    progress: 0,
    error:
      input.source === "screen_recording"
        ? "Recovered unsent recording. Retry upload, then send it to Orbit."
        : "Recovered unsent file. Retry upload, then send it to Orbit.",
  };
}

export function getDeliveryReceiptText(status: DeliveryReceiptStatus, attachmentCount = 0): string | null {
  switch (status) {
    case "uploading":
      return "Uploading attachment...";
    case "ready":
      return attachmentCount > 0
        ? `${attachmentCount} attachment${attachmentCount === 1 ? "" : "s"} ready. Send when finished.`
        : null;
    case "sending":
      return "Sending to Orbit...";
    case "sent":
      return attachmentCount > 0
        ? `Sent to Orbit with ${attachmentCount} attachment${attachmentCount === 1 ? "" : "s"}.`
        : "Sent to Orbit.";
    case "failed":
      return "Orbit could not finish sending. Your draft is still here.";
    case "idle":
    default:
      return null;
  }
}
